import type { LucideIcon } from "lucide-react";
import { AlertTriangle, PauseCircle, RotateCcw, Zap } from "lucide-react";
import type { ExecutionDiagnosticCause, ExecutionDiagnostics } from "@/api/model/irrigation-plan-execution-report";

import { formatDateTime } from "../utils";

/**
 * Presentation of each probable cause of a timed-out execution.
 */
const DIAGNOSTIC_CAUSE_CONFIG = {
  DEVICE_RESET: {
    label: "Reinício do dispositivo (reset)",
    shortLabel: "Reset",
    color: "bg-orange-100 text-orange-700",
    icon: RotateCcw,
  },
  VOLTAGE_DROP: {
    label: "Queda de tensão de alimentação",
    shortLabel: "Queda de tensão",
    color: "bg-yellow-100 text-yellow-700",
    icon: Zap,
  },
  SCHEDULING_INTERRUPTED: {
    label: "Execução interrompida pelo agendamento",
    shortLabel: "Agendamento interrompido",
    color: "bg-purple-100 text-purple-700",
    icon: AlertTriangle,
  },
  SCHEDULING_PAUSED: {
    label: "Agendamento pausado durante a execução",
    shortLabel: "Agendamento pausado",
    color: "bg-purple-100 text-purple-700",
    icon: PauseCircle,
  },
} as const satisfies Record<
  ExecutionDiagnosticCause,
  { label: string; shortLabel: string; color: string; icon: LucideIcon }
>;

/**
 * Formats a supply voltage given in millivolts as volts with the pt-BR decimal comma.
 *
 * @param millivolts - Voltage in millivolts.
 * @returns The voltage in volts, e.g. "11,2 V".
 */
function formatVoltage(millivolts: number | null | undefined): string {
  if (millivolts == null || !Number.isFinite(millivolts)) {
    return "-";
  }

  return `${(millivolts / 1000).toFixed(1).replace(".", ",")} V`;
}

/**
 * Tells whether the diagnosis holds any signal at all.
 *
 * @param diagnostics - Diagnosis of the execution.
 * @returns True when at least one signal was detected.
 */
export function hasDiagnosticSignals(diagnostics: ExecutionDiagnostics | undefined): boolean {
  if (!diagnostics) {
    return false;
  }

  return (
    diagnostics.device_reset_detected ||
    diagnostics.voltage_drop_detected ||
    diagnostics.scheduling_interrupted ||
    diagnostics.scheduling_paused
  );
}

interface DiagnosticCauseBadgeProps {
  diagnostics?: ExecutionDiagnostics;
}

/**
 * Compact badge showing the probable cause of an execution, used in the report table.
 */
export function DiagnosticCauseBadge({ diagnostics }: DiagnosticCauseBadgeProps) {
  const cause = diagnostics?.primary_cause;

  if (!cause) {
    return null;
  }

  const config = DIAGNOSTIC_CAUSE_CONFIG[cause];
  const CauseIcon = config.icon;

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      title={config.label}
    >
      <CauseIcon className="h-3 w-3" />
      {config.shortLabel}
    </div>
  );
}

interface ExecutionDiagnosticsSectionProps {
  diagnostics?: ExecutionDiagnostics;
}

/**
 * Section of the execution detail modal explaining why the execution did not finish.
 *
 * Renders the probable cause plus every supporting signal found in the `codec_event`
 * timeline of the project's LIC (device reset, supply voltage drop, scheduling
 * interruption or pause).
 */
export function ExecutionDiagnosticsSection({ diagnostics }: ExecutionDiagnosticsSectionProps) {
  if (!hasDiagnosticSignals(diagnostics) || !diagnostics) {
    return (
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Diagnóstico da execução</h4>
        <p className="text-sm text-gray-500">
          Nenhuma causa identificada nos eventos do dispositivo durante a execução.
        </p>
      </div>
    );
  }

  const causeConfig = diagnostics.primary_cause ? DIAGNOSTIC_CAUSE_CONFIG[diagnostics.primary_cause] : null;

  return (
    <div className="border-t pt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Diagnóstico da execução</h4>

      <div className="space-y-3 text-sm">
        {causeConfig && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">Causa provável</div>
            <div className="mt-1 font-medium text-gray-800">{causeConfig.label}</div>
          </div>
        )}

        {diagnostics.device_reset_detected && (
          <div className="border-b border-gray-200 pb-2">
            <div className="font-bold text-gray-700">Reinício do dispositivo (reset)</div>
            <div className="text-xs text-orange-700">
              Detectado em {formatDateTime(diagnostics.device_reset_detected_at)}
            </div>
            {diagnostics.device_reset_count_before != null && diagnostics.device_reset_count_after != null && (
              <div className="text-xs text-gray-600">
                Contador de resets: {diagnostics.device_reset_count_before} → {diagnostics.device_reset_count_after}
              </div>
            )}
          </div>
        )}

        {diagnostics.voltage_drop_detected && (
          <div className="border-b border-gray-200 pb-2">
            <div className="font-bold text-gray-700">Tensão de alimentação</div>
            <div className="text-xs text-yellow-700">
              Queda de tensão registrada em {formatDateTime(diagnostics.voltage_drop_detected_at)}
            </div>
            <div className="text-xs text-gray-600">
              {formatVoltage(diagnostics.voltage_at_drop_mv)} (anterior:{" "}
              {formatVoltage(diagnostics.previous_voltage_mv)})
            </div>
          </div>
        )}

        {diagnostics.scheduling_interrupted && (
          <div className="border-b border-gray-200 pb-2">
            <div className="font-bold text-gray-700">Agendamento</div>
            <div className="text-xs text-purple-700">
              Execução interrompida pelo dispositivo em {formatDateTime(diagnostics.scheduling_interrupted_at)}
            </div>
          </div>
        )}

        {diagnostics.scheduling_paused && (
          <div className="border-b border-gray-200 pb-2">
            <div className="font-bold text-gray-700">Agendamento</div>
            <div className="text-xs text-purple-700">
              Agendamento pausado em {formatDateTime(diagnostics.scheduling_paused_at)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
